import { makeAutoObservable, reaction } from 'mobx';
import { calendarStore } from '../../store/calendar-store';
import {
  BookingModel,
  BookingDetail,
  BookingResponse,
  BookingListItem,
} from '../../model/professional/booking-model';
import { CalendarDisplayEvent } from '../../types/calendar-types';
import { getStatusColor } from '../../types/calendar-types';
import {formatDateInTimeZone, utcToLocalTime} from '../../utils/timezone';
import toastStore from "../../store/toast-store";
import {HTTP_STATUS_CODE} from "../../utils/UHttp";
import {updateStatusBookingShareStore} from "../../utils/action-share-store";

/**
 * ViewModel pour le calendrier qui utilise l'approche hybride
 * Combinant un view-model observable avec un store séparé
 */
class CalendarViewModel {
  isLoading: boolean = false;
  error: string | null = null;
  isCancelling: boolean = false
  currentDate: Date = new Date();
  showEventDetails: boolean = false;
  showNewEventModal: boolean = false;
  showEditManualBookingModal: boolean = false;
  bookingToEdit: BookingDetail | null = null;
  newEventDate: Date | undefined = undefined;
  newEventTime: string | undefined = undefined;
  currentView: 'day' | 'week' = 'week';
  selectedCollaboratorId: string = '';
  protected bookingModel: BookingModel;

  constructor() {
    makeAutoObservable(this);
    this.bookingModel = BookingModel.getInstance();

    reaction(
      () => this.selectedCollaboratorId,
      (collaboratorId) => {
        if (collaboratorId) {
          this.fetchProfessionalBookings();
        }
      }
    );
  }

  get getIsCancelling(){
    return this.isCancelling
  }

  get getCurrentDate(){
    return this.currentDate
  }

  get getShowEventDetails(){
    return this.showEventDetails
  }

  get getShowNewEventModal(){
    return this.showNewEventModal
  }

  get getShowEditManualBookingModal(){
    return this.showEditManualBookingModal
  }

  get getBookingToEdit(){
    return this.bookingToEdit
  }

  get getNewEventDate(){
    return this.newEventDate
  }

  get getNewEventTime(){
    return this.newEventTime
  }

  get getCurrentView(){
    return this.currentView
  }

  get getDaysOfWeek ()  {
    const days = [];
    const startOfWeek = new Date(this.getCurrentDate);
    const day = this.getCurrentDate.getDay();
    const diff = this.getCurrentDate.getDate() - day + (day === 0 ? -6 : 1); // Ajuster si dimanche
    startOfWeek.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date);
    }

    return days;
  };

  isToday(date: Date): boolean {
    const today = new Date();
    const dateStr = formatDateInTimeZone(date, 'yyyy-MM-dd');
    const todayStr = formatDateInTimeZone(today, 'yyyy-MM-dd');
    return dateStr === todayStr;
  }

  get getDayAppointments() {
    return calendarStore.calendarEvents.filter(appointment => {
      const appointmentDate = new Date(appointment.start);
      const currentDateStr = formatDateInTimeZone(this.currentDate, 'yyyy-MM-dd');
      const appointmentDateStr = formatDateInTimeZone(appointmentDate, 'yyyy-MM-dd');
      return currentDateStr === appointmentDateStr;
    });
  }

  get generateTimeSlots ()  {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        slots.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    return slots;
  };

  setIsCancelling() {
    this.isCancelling = !this.isCancelling
  }

  setCurrentDate(date: Date) {
    this.currentDate = date;
  }

  setShowEventDetails(show: boolean) {
    this.showEventDetails = show;
  }

  setShowNewEventModal(show: boolean) {
    this.showNewEventModal = show;
  }

  setNewEventDate(date: Date | undefined) {
    this.newEventDate = date;
  }

  setNewEventTime(time: string | undefined) {
    this.newEventTime = time;
  }

  setCurrentView(view: 'day' | 'week') {
    this.currentView = view;
  }

  setSelectedCollaboratorId(id: string): void {
    this.selectedCollaboratorId = id;
  }

  /**
   * Définit l'état de chargement
   */
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  /**
   * Définit le message d'erreur
   */
  setError = (error: string | null) => {
    this.error = error;
  };

  /**
   * Convertit les réservations en événements de calendrier
   */
  convertToCalendarEvents() {
    this.setIsLoading(true);
    
    try {
      const timeSlotMap = new Map<string, { booking: any, createdAt: Date }>();
      const sortedBookings = [...calendarStore.bookings].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB.getTime() - dateA.getTime();
      });
      
      sortedBookings.forEach(booking => {
        if (!booking.startTime || !booking.endTime) {
          return;
        }
        
        // Créer une clé unique pour ce créneau horaire
        const timeSlotKey = `${booking.startTime}-${booking.endTime}`;
        
        if (!timeSlotMap.has(timeSlotKey)) {
          timeSlotMap.set(timeSlotKey, {
            booking,
            createdAt: new Date(booking.createdAt || 0)
          });
        }
      });
      
      const events: CalendarDisplayEvent[] = [];
      
      timeSlotMap.forEach(({ booking }, key) => {
        const startDate = utcToLocalTime(booking.startTime);
        const endDate = utcToLocalTime(booking.endTime);
        
        const event: CalendarDisplayEvent = {
          id: booking.id,
          title: booking.serviceName,
          start: startDate,
          end: endDate,
          status: booking.status,
          color: getStatusColor(booking.status),
          bookingId: booking.id,
          isManualBooking: booking.isManualBooking
        };
        
        events.push(event);
      });
      
      calendarStore.setCalendarEvents(events);
      this.setError(null);
    } catch (error) {
      this.setError('Erreur lors de la conversion des événements');
      return [];
    } finally {
      this.setIsLoading(false);
    }
  }

  getWeekNumber = (date: Date): { startDate: Date, endDate: Date, weekNumber: number } => {
    const tempDate = new Date(date);
    const firstDayOfYear = new Date(tempDate.getFullYear(), 0, 1);
    const pastDaysOfYear = (tempDate.getTime() - firstDayOfYear.getTime()) / 86400000;
    const weekNumber = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);

    const startDate = new Date(tempDate);
    const currentDay = startDate.getDay(); // 0 = dimanche, 1 = lundi, etc.
    const diff = currentDay === 0 ? 6 : currentDay - 1; // Ajustement pour que lundi soit le premier jour
    startDate.setDate(startDate.getDate() - diff);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
    endDate.setHours(23, 59, 59, 999);
    
    return { startDate, endDate, weekNumber };
  };

  formatDate = (d: Date) => {
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  async fetchProfessionalBookings(startDate?: Date, endDate?: Date) {
    this.setIsLoading(true);
    this.setError(null);

    if (!this.selectedCollaboratorId) {
      this.setIsLoading(false);
      return;
    }

    try {
        // Si les dates ne sont pas fournies, on utilise la date actuelle pour obtenir la semaine
        if (!startDate || !endDate) {
          const { startDate: weekStart, endDate: weekEnd } = this.getWeekNumber(this.getCurrentDate);
          startDate = weekStart;
          endDate = weekEnd;
        }
        const response: BookingResponse = await this.bookingModel.getProfessionalBookingsByDate(
          startDate.toISOString(),
          endDate.toISOString(),
          this.selectedCollaboratorId
        );
        if(response.status === HTTP_STATUS_CODE.OK) {
          const bookings = response.data as BookingListItem[] || [];
          calendarStore.setBookings(bookings)
          this.convertToCalendarEvents()
        }

    } catch (error: any) {
      toastStore.error(error?.response?.data?.message);
    } finally {
      this.setIsLoading(false);
    }
  }

  /**
   * Met à jour le statut d'une réservation
   */
  /**
   * Gère le changement de statut d'un rendez-vous
   *
   * Met à jour le statut d'un rendez-vous spécifique identifié par son ID
   * et déclenche un rendu UI via MobX
   *
   * @param id - Identifiant unique du rendez-vous à modifier

   */
  async handleStatusChange(id: string): Promise<void> {
    try {
      if(!id) return
      const response = await this.bookingModel.cancelBooking(id);

      if(response.status === HTTP_STATUS_CODE.OK ){
        toastStore.success(response.message);
        this.closeEventDetails();
        updateStatusBookingShareStore(id, "cancelled")
      } else {
        toastStore.error(response.message);
      }

    } catch (error:any) {
      console.error(error?.response?.data?.message)
      toastStore.error(error?.response?.data?.message);
    }
  }

  /**
   * Navigation dans le calendrier
   */
  navigateCalendar(direction: 'prev' | 'next' | 'today') {
    const currentDate = this.currentDate;
    let newDate: Date;
    
    if (direction === 'prev') {
      if (this.currentView === 'day') {
        // Reculer d'un jour
        const prevDay = new Date(currentDate);
        prevDay.setDate(prevDay.getDate() - 1);
        newDate = prevDay;
      } else {
        const prevWeek = new Date(currentDate);
        prevWeek.setDate(prevWeek.getDate() - 7);
        newDate = prevWeek;
      }
    } else if (direction === 'next') {
      if (this.currentView === 'day') {
        // Avancer d'un jour
        const nextDay = new Date(currentDate);
        nextDay.setDate(nextDay.getDate() + 1);
        newDate = nextDay;
      } else {
        const nextWeek = new Date(currentDate);
        nextWeek.setDate(nextWeek.getDate() + 7);
        newDate = nextWeek;
      }
    } else {
      // Aujourd'hui
      newDate = new Date();
      newDate.setHours(0, 0, 0, 0);
    }
    
    this.setCurrentDate(newDate);
  }

  /**
   * Ouvre le modal de détails d'un événement
   */
  async openEventDetails(event: CalendarDisplayEvent) {
    this.setIsLoading(true);
    this.setError(null);
    calendarStore.setSelectedEvent(event);
    
    if (event.bookingId) {
      try {
        const response = await this.bookingModel.getBookingById(event.bookingId);

        if (response.success && response.data) {
          const bookingDetail = response.data as BookingDetail;
          calendarStore.setSelectedBooking(bookingDetail);
          this.setShowEventDetails(true);
        } else {
          this.setError(response.message);
        }
      } catch (error) {
        this.setError('Erreur lors de la récupération des détails');
      } finally {
        this.setIsLoading(false);
      }
    } else {
      this.setIsLoading(false);
    }
  }

  /**
   * Ferme le modal de détails
   */
  closeEventDetails() {
    this.setShowEventDetails(false);
    this.isCancelling = false
  }

  /**
   * Ouvre le modal de création de rendez-vous
   */
  openNewEventModal() {
    this.setNewEventDate(this.currentDate);
    this.setNewEventTime('09:00');
    this.setShowNewEventModal(true);
  }

  /**
   * Ferme le modal de création de rendez-vous
   */
  closeNewEventModal() {
    this.setShowNewEventModal(false);
  }

  /**
   * Ouvre le modal d'édition de booking manuel
   */
  openEditManualBookingModal(booking: BookingDetail) {
    this.bookingToEdit = booking;
    this.showEditManualBookingModal = true;
  }

  /**
   * Ferme le modal d'édition de booking manuel
   */
  closeEditManualBookingModal() {
    this.showEditManualBookingModal = false;
    this.bookingToEdit = null;
  }

  /**
   * Change la vue du calendrier (jour/semaine)
   */
  changeView(view: 'day' | 'week') {
    this.setCurrentView(view);
  }
}

const calendarViewModel_ = new CalendarViewModel()
export const useCalendarViewModel = () => {
  return calendarViewModel_
}

export const calendarViewModel = new CalendarViewModel();
